import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { format } from 'date-fns';
import { etapaPorId } from '@/lib/constants';
import { origemPorId } from '@/leads/origens';
import { mostraOportunidade } from '@/leads/oportunidade';
import { formatarTelefone } from '@/services/leadsService';
import { aparicaoNaJanela } from '@/leads/resgate';
import { nomeCurto } from '@/equipe/nomeCurto';

/**
 * ★ VI — a TABELA da lista de leads: as 12 colunas do dono, nesta ordem, e o
 * CSV com as mesmas colunas na mesma ordem.
 *
 * Só LEITURA de campos já gravados no lead (+ o nome atual do SDR pela equipe e
 * o rótulo da origem pela config). Dinheiro NÃO se recalcula aqui:
 * `valorRecebido` é o derivado que a VA mantém no próprio lead; a subcoleção
 * `recebimentos` não é lida. `leadsPage.grafo.test.js` percorre os imports
 * deste arquivo e falha se alcançar metas/remuneração/relatórios/serviços de
 * dinheiro — por isso a tabela vive fora da página: a página monta o
 * `PainelLead` (a ficha), que legitimamente lê recebimentos.
 *
 * Os quatro valores têm significados diferentes (VE/VA), e o cabeçalho e a
 * legenda dizem qual é qual. "Valor de oportunidade" só aparece depois de
 * compareceu (`mostraOportunidade`): antes disso é "—" na tela e vazio no CSV.
 */

/**
 * ★ VI-1 — LARGURAS FIXAS por coluna (`<colgroup>` + `table-layout: fixed`).
 *
 * Medido em Chrome (1440 e 1920) antes do conserto: a célula do Nome, com
 * `white-space: nowrap` e sem limite, chegava a 434 px com um nome comprido —
 * e, como ela é FIXA à esquerda, ao rolar a tabela ela cobria sete colunas
 * (WhatsApp … Etapa) de uma vez: o dono via "Nome" seguido de "Valor de
 * oportunidade" e lia como "nome muito largo". Com largura limitada (220 px +
 * reticências, nome inteiro no `title`) e a soma das colunas como largura
 * mínima da tabela, a tabela ROLA de fato em vez de esmagar, e o Nome fixo
 * cobre no máximo uma coluna e meia por vez.
 */
export const LARGURA_CHECK = 32;
export const LARGURA_ACAO = 110;
/** Título (tooltip) de uma coluna; o do código depende do nome do sistema da clínica. */
export const tituloDaColuna = (c, config = null) => (typeof c.titulo === 'function' ? c.titulo(config) : c.titulo);

export const COLUNAS = [
  { id: 'nome', rotulo: 'Nome', titulo: 'Nome do lead', largura: 220 },
  // VK: o rótulo é "Código Paciente"; o campo continua `codigoCareflow` (é o mesmo código do sistema Careflow).
  // VX: o título cita o sistema da clínica pela config (`tituloDaColuna`), nunca a marca fixa. O campo continua `codigoCareflow`.
  { id: 'codigoCareflow', rotulo: 'Código Paciente', titulo: (config) => `Código do paciente (o mesmo usado no ${nomeDoSistema(config)})`, largura: 110 },
  { id: 'telefone', rotulo: 'WhatsApp', titulo: 'Telefone do WhatsApp', largura: 140 },
  { id: 'sdr', rotulo: 'SDR', titulo: 'SDR responsável (nome atual na equipe)', largura: 120 },
  { id: 'criadoEm', rotulo: '1º contato', titulo: 'Data em que a conversa começou (ou em que o lead foi cadastrado à mão)', largura: 90 },
  { id: 'origem', rotulo: 'Origem', titulo: 'De onde o lead veio', largura: 130 },
  { id: 'procedimento', rotulo: 'Procedimento', titulo: 'Tratamento de interesse', largura: 130 },
  { id: 'etapa', rotulo: 'Etapa', titulo: 'Etapa do funil', largura: 130 },
  { id: 'valorOrcado', rotulo: 'Valor de oportunidade', titulo: 'Valor de oportunidade — o orçado ao paciente, só depois de compareceu', num: true, largura: 130 },
  { id: 'valorFechado', rotulo: 'Total da venda', titulo: 'Total da venda — o que virou contrato', num: true, largura: 130 },
  { id: 'valorEntrada', rotulo: 'Valor de entrada', titulo: 'Pago no ato do fechamento (caixa do dia; não entra na comissão)', num: true, largura: 130 },
  { id: 'valorRecebido', rotulo: 'Valor recebido', titulo: 'Soma dos recebimentos importados (base da comissão)', num: true, largura: 130 },
];

/** Largura mínima da tabela: a soma de tudo — abaixo disso ela rola, não esmaga. */
export const LARGURA_MINIMA = LARGURA_CHECK + LARGURA_ACAO + COLUNAS.reduce((t, c) => t + c.largura, 0);

export const LEGENDA_VALORES =
  'Oportunidade = valor orçado (a proposta; só depois de compareceu) · Fechado = total do contrato · '
  + 'Entrada = pago no ato (caixa do dia; não entra na comissão) · Recebido = soma dos recebimentos importados (base da comissão)';

const moeda = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const paraData = (v) => (typeof v?.toDate === 'function' ? v.toDate() : v instanceof Date ? v : null);
const dataCurta = (v) => { const d = paraData(v); return d ? format(d, 'dd/MM/yy') : ''; };
const numero = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0; };

/** Nome ATUAL do SDR pela equipe; o `sdrNome` gravado é só reserva (a pessoa pode ter sido renomeada). */
export function nomeDoSdr(lead, equipe = []) {
  const atual = lead?.sdrId ? (equipe ?? []).find((u) => u.id === lead.sdrId) : null;
  return atual?.nome ?? lead?.sdrNome ?? '';
}

/** Oportunidade para a lista: o valor só depois de compareceu; senão 0 (vira "—"/vazio). */
const oportunidadeDe = (lead) => (mostraOportunidade(lead) ? numero(lead?.valorOrcado) : 0);

/** Os 12 valores CRUS de uma linha, na ordem das colunas — a tela formata, o CSV escreve. */
export function valoresDaLinha(lead, { equipe = [], config = null } = {}) {
  const origem = origemPorId(config, lead?.origem);
  return {
    nome: lead?.nome ?? '',
    codigoCareflow: lead?.codigoCareflow ?? '',
    telefone: lead?.telefone ?? '',
    sdr: nomeDoSdr(lead, equipe),
    criadoEm: dataCurta(lead?.criadoEm),
    origem: origem.vazia ? '' : origem.label,
    procedimento: lead?.procedimento ?? '',
    etapa: etapaPorId(lead?.etapa).label,
    valorOrcado: oportunidadeDe(lead),
    valorFechado: numero(lead?.valorFechado),
    valorEntrada: numero(lead?.valorEntrada),
    valorRecebido: numero(lead?.valorRecebido),
  };
}

/** O que a ordenação compara em cada coluna: dinheiro numérico, data por tempo, texto normalizado. */
export function chaveDeOrdenacao(lead, campo, { equipe = [], config = null } = {}) {
  if (campo === 'criadoEm') return paraData(lead?.criadoEm)?.getTime() ?? 0;
  if (campo === 'valorOrcado') return oportunidadeDe(lead);
  if (campo === 'valorFechado' || campo === 'valorEntrada' || campo === 'valorRecebido') return numero(lead?.[campo]);
  const v = valoresDaLinha(lead, { equipe, config })[campo] ?? '';
  return String(v).normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase();
}

/** CSV da seleção — Excel brasileiro espera `;`; BOM para os acentos. Mesmas 12 colunas, mesma ordem. */
export function textoCsv(leads, { equipe = [], config = null } = {}) {
  const escapar = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const cabecalho = COLUNAS.map((c) => escapar(c.rotulo)).join(';');
  const linhas = leads.map((l) => {
    const v = valoresDaLinha(l, { equipe, config });
    return COLUNAS.map((c) => escapar(c.num ? (v[c.id] > 0 ? v[c.id] : '') : v[c.id])).join(';');
  });
  return '\ufeff' + [cabecalho, ...linhas].join('\r\n');
}

export function exportarCsv(leads, opcoes) {
  const csv = textoCsv(leads, opcoes);
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `leads-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

const ou = (v) => (v === '' || v === 0 || v === null || v === undefined ? '—' : v);

/**
 * ★★ Nome do lead / coluna SDR (28/out) — toda célula corta com reticências (`global.css`, `.tabela-leads
 * td`), então todo valor vai inteiro no `title`. A SDR aparece pelo nome CURTO, como na Agenda: medido, 4
 * dos 5 nomes de SDR de Campinas passavam de 96 px e eram desenhados por cima de "1º contato"
 * (`docs/medicao-nome-lead.md` §2). O CSV continua com o nome inteiro (`valoresDaLinha`).
 */
const dica = (v) => (v ? String(v) : undefined);

export default function TabelaLeads({
  janela = null, // VR: a janela do período, para o selo "resgate"
  leads, equipe, config, ordem, aoOrdenar, selecionados, aoAlternar, todosMarcados, aoAlternarTodos, aoAbrir,
}) {
  return (
    <div className="tabela-envolve">
      <table className="tabela tabela-leads" style={{ minWidth: `${LARGURA_MINIMA}px` }}>
        <colgroup>
          <col className="col-check" style={{ width: `${LARGURA_CHECK}px` }} />
          {COLUNAS.map((c) => (
            <col key={c.id} data-coluna={c.id} style={{ width: `${c.largura}px` }} />
          ))}
          <col className="col-acao" style={{ width: `${LARGURA_ACAO}px` }} />
        </colgroup>
        <thead>
          <tr>
            <th className="col-check col-fixa-esq">
              <input type="checkbox" checked={todosMarcados} onChange={aoAlternarTodos} aria-label="Selecionar todos" />
            </th>
            {COLUNAS.map((c, i) => (
              <th key={c.id} className={[i === 0 ? 'col-fixa-esq col-nome' : '', c.num ? 'num' : ''].join(' ').trim() || undefined} title={tituloDaColuna(c, config)}>
                <button type="button" className="th-ordenar" onClick={() => aoOrdenar(c.id)} title={tituloDaColuna(c, config)}>
                  {c.rotulo}
                  {ordem.campo === c.id && <span>{ordem.desc ? ' ↓' : ' ↑'}</span>}
                </button>
              </th>
            ))}
            <th className="col-fixa-dir col-acao" aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => {
            const v = valoresDaLinha(lead, { equipe, config });
            return (
              <tr key={lead.id} className={selecionados.has(lead.id) ? 'selecionada' : undefined} onClick={() => aoAbrir(lead.id)}>
                <td className="col-check col-fixa-esq" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={selecionados.has(lead.id)} onChange={() => aoAlternar(lead.id)} aria-label={`Selecionar ${lead.nome}`} />
                </td>
                <td className="col-fixa-esq col-nome" title={v.nome || undefined}>{ou(v.nome)}{aparicaoNaJanela(lead, janela) === 'resgate' && <span className="pilula-inferido selo-resgate">resgate</span>}</td>
                <td title={dica(v.codigoCareflow)}>{ou(v.codigoCareflow)}</td>
                <td title={dica(v.telefone && formatarTelefone(v.telefone))}>{v.telefone ? formatarTelefone(v.telefone) : '—'}</td>
                <td className="col-sdr" title={dica(v.sdr)}>{ou(nomeCurto(v.sdr, equipe))}</td>
                <td title={dica(v.criadoEm)}>{ou(v.criadoEm)}</td>
                <td title={dica(v.origem)}>{ou(v.origem)}</td>
                <td title={dica(v.procedimento)}>
                  {ou(v.procedimento)}
                  {v.procedimento && lead.anuncioInferido === true && <span className="pilula-inferido">inferido</span>}
                </td>
                <td title={dica(v.etapa)}>
                  <span className="pilula pequena" style={{ background: etapaPorId(lead.etapa).cor }}>{v.etapa}</span>
                </td>
                <td className="num" title={dica(v.valorOrcado > 0 && moeda(v.valorOrcado))}>{v.valorOrcado > 0 ? moeda(v.valorOrcado) : '—'}</td>
                <td className="num" title={dica(v.valorFechado > 0 && moeda(v.valorFechado))}>{v.valorFechado > 0 ? moeda(v.valorFechado) : '—'}</td>
                <td className="num" title={dica(v.valorEntrada > 0 && moeda(v.valorEntrada))}>{v.valorEntrada > 0 ? moeda(v.valorEntrada) : '—'}</td>
                <td className="num" title={dica(v.valorRecebido > 0 && moeda(v.valorRecebido))}>{v.valorRecebido > 0 ? moeda(v.valorRecebido) : '—'}</td>
                <td className="col-fixa-dir col-acao" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="btn-secundario compacto" onClick={() => aoAbrir(lead.id)}>
                    Abrir ficha
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {leads.length === 0 && <p className="vazio">Nenhum lead com esses filtros.</p>}
      <p className="legenda-valores">{LEGENDA_VALORES}</p>
    </div>
  );
}
